import * as THREE from 'three';
import {
  ACCESSORIES,
  BACKPACKS,
  BODY_MODELS,
  DEFAULT_CHARACTER,
  EXPRESSIONS,
  GLASSES,
  HAIR_COLORS,
  HAIR_STYLES,
  HATS,
  PALETTE,
  SHIRTS,
  SKIN_TONES,
  findLockedCosmetic,
  randomCharacter,
  type CharacterConfig,
  type CosmeticOption,
} from '@kokun/shared';
import { Avatar } from '../game/characters/avatar';
import { faceCache } from '../game/characters/face-cache';
import { api } from '../app/api';
import { ui } from '../app/game-store';
import { audio } from '../game/audio/audio';
import { button, clear, h, hex, modal, toast } from '../ui/dom';
import { autoCrop, encodeFace, loadPhoto, PhotoError, renderFace, type Crop, type FaceStyle } from './face-processor';

type Tab = 'model' | 'photo' | 'look' | 'clothes' | 'extras';

/**
 * Editor de personajes: modelo base → foto (consentimiento, recorte, estilo) → aspecto → ropa →
 * extras, con vista previa 3D en directo. Guarda en el servidor (que vuelve a validar todo).
 */
export function openCreator(onDone: () => void) {
  const profile = ui.get().profile!;
  const level = profile.player.level;
  let cfg: CharacterConfig = { ...DEFAULT_CHARACTER, ...profile.player.character };
  let faceRev = profile.player.faceRev;
  let faceCanvas: HTMLCanvasElement | ImageBitmap | null = null;
  let tab: Tab = 'model';

  // --- Vista previa 3D (renderer propio, ligero)
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
  const scene = new THREE.Scene();
  scene.add(new THREE.HemisphereLight(0xffffff, 0x8899aa, 1.6));
  const key = new THREE.DirectionalLight(0xffffff, 2);
  key.position.set(2, 4, -3);
  scene.add(key);
  const cam = new THREE.PerspectiveCamera(35, 1, 0.1, 50);
  cam.position.set(0, 1.5, -4.6);
  cam.lookAt(0, 1.05, 0);
  const avatar = new Avatar(cfg);
  scene.add(avatar.root);
  const floor = new THREE.Mesh(new THREE.CircleGeometry(1.3, 40), new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.35 }));
  floor.rotation.x = -Math.PI / 2;
  scene.add(floor);
  let spin = 0;
  let dragging = false;
  let lastX = 0;
  let raf = 0;
  let previewEmote = 0;
  const loop = () => {
    raf = requestAnimationFrame(loop);
    if (!dragging) spin += 0.004;
    avatar.root.rotation.y = spin;
    previewEmote += 1 / 60;
    avatar.state = Math.floor(previewEmote / 4) % 3 === 1 ? 'emote:wave' : 'idle';
    avatar.update(1 / 60);
    renderer.render(scene, cam);
  };

  if (faceRev > 0)
    void faceCache.get(profile.player.id, faceRev).then((img) => {
      if (img) {
        faceCanvas = img;
        avatar.setFaceImage(img);
      }
    });

  const preview = h('div', { class: 'creator-preview' }, renderer.domElement);
  preview.addEventListener('pointerdown', (e) => {
    dragging = true;
    lastX = e.clientX;
  });
  window.addEventListener('pointerup', () => (dragging = false));
  preview.addEventListener('pointermove', (e) => {
    if (dragging) {
      spin += (e.clientX - lastX) * 0.01;
      lastX = e.clientX;
    }
  });

  const panel = h('div', { class: 'creator-panel panel' });
  const tabs = h('div', { class: 'tabs' });
  const body = h('div', { class: 'creator-body' });
  const root = h(
    'div',
    { class: 'creator screen' },
    h('header', { class: 'creator-head' }, h('h1', { class: 'title-small' }, 'Crea a tu Kokun'), h('p', { class: 'muted' }, 'Arrastra el personaje para girarlo')),
    h('div', { class: 'creator-main' }, preview, panel),
  );
  panel.append(tabs, body, h('div', { class: 'creator-actions' }, button('🎲 Aleatorio', () => update(randomCharacter()), 'btn ghost'), button('Cancelar', () => close(false), 'btn ghost'), button('💾 Guardar personaje', () => void save(), 'btn primary')));

  const update = (patch: Partial<CharacterConfig>) => {
    cfg = { ...cfg, ...patch };
    avatar.build(cfg);
    avatar.setFaceImage(faceCanvas);
    audio.play('click');
    render();
  };

  const optionGrid = (title: string, options: CosmeticOption[], current: string, onPick: (id: string) => void) =>
    h(
      'div',
      { class: 'opt-group' },
      h('h3', null, title),
      h(
        'div',
        { class: 'opt-grid' },
        ...options.map((o) => {
          const locked = o.unlockLevel > level;
          return h(
            'button',
            {
              class: `opt${o.id === current ? ' on' : ''}${locked ? ' locked' : ''}`,
              type: 'button',
              title: locked ? `Se desbloquea en el nivel ${o.unlockLevel}` : o.name,
              onClick: () => (locked ? toast(`🔒 «${o.name}» se desbloquea en el nivel ${o.unlockLevel}. ¡Sigue jugando!`) : onPick(o.id)),
            },
            locked ? `🔒 ${o.name}` : o.name,
          );
        }),
      ),
    );

  const swatches = (title: string, colors: number[], current: number, onPick: (i: number) => void) =>
    h(
      'div',
      { class: 'opt-group' },
      h('h3', null, title),
      h(
        'div',
        { class: 'swatches' },
        ...colors.map((c, i) =>
          h('button', { class: `swatch${i === current ? ' on' : ''}`, type: 'button', style: { background: hex(c) }, 'aria-label': `Color ${i + 1}`, onClick: () => onPick(i) }),
        ),
      ),
    );

  // --- Paso de la foto
  const photoStep = () => {
    const box = h('div', { class: 'photo-step' });
    box.append(
      h('p', null, 'Sube una foto de tu cara mirando a cámara, con buena luz. Se convierte en la cara de tu Kokun.'),
      h(
        'ul',
        { class: 'privacy-list' },
        h('li', null, '🔒 La foto original se procesa en TU navegador y nunca se sube.'),
        h('li', null, '🖼️ Solo se guarda un recorte de 256×256 px, sin metadatos (GPS, EXIF…).'),
        h('li', null, '👀 Solo lo ven los jugadores de tu misma sala. No es público ni indexable.'),
        h('li', null, '🗑️ Puedes borrarlo cuando quieras. Se elimina solo tras 180 días sin jugar si no tienes cuenta.'),
        h('li', null, '🚫 No se usa para entrenar modelos ni para ningún otro fin.'),
      ),
    );
    const file = h('input', { type: 'file', accept: 'image/jpeg,image/png,image/webp', class: 'hidden-file' }) as HTMLInputElement;
    file.addEventListener('change', () => {
      const f = file.files?.[0];
      file.value = '';
      if (f) askConsent(f);
    });
    box.append(
      h(
        'div',
        { class: 'row' },
        button('📷 Subir foto', () => file.click(), 'btn primary'),
        faceRev > 0 || faceCanvas
          ? button('🗑️ Quitar mi cara', async () => {
              try {
                await api.deleteFace();
                faceRev = 0;
                faceCanvas = null;
                faceCache.drop(profile.player.id);
                avatar.setFaceImage(null);
                toast('Rostro eliminado del servidor.', 'success');
                render();
              } catch (e) {
                toast((e as Error).message, 'error');
              }
            }, 'btn danger')
          : null,
      ),
      file,
      h('label', { class: 'check' }, h('input', { type: 'checkbox', checked: cfg.useFace, onChange: (e: Event) => update({ useFace: (e.target as HTMLInputElement).checked }) }), ' Usar mi cara en el personaje'),
      h('p', { class: 'muted small' }, 'Normas: solo tu propia cara. Nada de fotos de otras personas, famosos, desnudos ni contenido ofensivo. Los jugadores pueden reportar avatares inapropiados.'),
    );
    return box;
  };

  const askConsent = (f: File) => {
    modal(
      'Antes de usar tu foto',
      [
        h('p', null, 'Vamos a procesar esta foto en tu dispositivo para crear la textura de la cara de tu personaje.'),
        h('p', null, 'Se enviará al servidor ÚNICAMENTE el recorte final (256×256), que verán solo los jugadores de tu sala. Puedes borrarlo en cualquier momento.'),
        h('p', null, 'Confirmo que la foto es mía y que acepto este uso.'),
      ],
      [
        { label: 'Cancelar', cls: 'btn ghost', run: () => undefined },
        { label: 'Acepto, continuar', cls: 'btn primary', run: () => void startCrop(f) },
      ],
    );
  };

  const startCrop = async (f: File) => {
    let img: ImageBitmap;
    try {
      img = await loadPhoto(f);
    } catch (e) {
      toast(e instanceof PhotoError ? e.message : 'No se pudo leer la foto.', 'error');
      return;
    }
    const crop: Crop = await autoCrop(img);
    const style: FaceStyle = { cartoon: false, brightness: 1, saturation: 1.1 };
    const view = h('canvas', { class: 'crop-canvas', width: 320, height: 320 }) as HTMLCanvasElement;
    const out = h('canvas', { class: 'crop-out', width: 128, height: 128 }) as HTMLCanvasElement;
    const draw = () => {
      const ctx = view.getContext('2d')!;
      const scale = 320 / (crop.size * 1.6);
      ctx.fillStyle = '#111';
      ctx.fillRect(0, 0, 320, 320);
      ctx.drawImage(img, crop.cx - 160 / scale, crop.cy - 160 / scale, 320 / scale, 320 / scale, 0, 0, 320, 320);
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      ctx.beginPath();
      ctx.rect(0, 0, 320, 320);
      ctx.ellipse(160, 160, (crop.size * scale * 0.86) / 2, (crop.size * scale) / 2, 0, 0, Math.PI * 2, true);
      ctx.fill('evenodd');
      ctx.strokeStyle = '#ffd60a';
      ctx.lineWidth = 3;
      ctx.setLineDash([8, 6]);
      ctx.beginPath();
      ctx.ellipse(160, 160, (crop.size * scale * 0.86) / 2, (crop.size * scale) / 2, 0, 0, Math.PI * 2);
      ctx.stroke();
      const face = renderFace(img, crop, style);
      out.getContext('2d')!.clearRect(0, 0, 128, 128);
      out.getContext('2d')!.drawImage(face, 0, 0, 128, 128);
      avatar.setFaceImage(face);
    };
    let drag: { x: number; y: number } | null = null;
    view.addEventListener('pointerdown', (e) => {
      drag = { x: e.clientX, y: e.clientY };
      view.setPointerCapture(e.pointerId);
    });
    view.addEventListener('pointermove', (e) => {
      if (!drag) return;
      const k = (crop.size * 1.6) / 320;
      crop.cx -= (e.clientX - drag.x) * k;
      crop.cy -= (e.clientY - drag.y) * k;
      drag = { x: e.clientX, y: e.clientY };
      draw();
    });
    view.addEventListener('pointerup', () => (drag = null));
    const slider = (label: string, min: number, max: number, step: number, value: number, set: (v: number) => void) =>
      h('label', { class: 'slider' }, label, h('input', { type: 'range', min, max, step, value, onInput: (e: Event) => (set(Number((e.target as HTMLInputElement).value)), draw()) }));
    const minSize = Math.min(img.width, img.height) * 0.15;
    const maxSize = Math.min(img.width, img.height);
    const closeCrop = modal(
      'Ajusta tu cara',
      [
        h('p', { class: 'muted' }, 'Arrastra la foto para centrar tu cara dentro del óvalo.'),
        h('div', { class: 'crop-row' }, view, h('div', null, h('p', { class: 'small' }, 'Resultado'), out)),
        slider('Zoom', 0, 1, 0.01, 1 - (crop.size - minSize) / (maxSize - minSize), (v) => (crop.size = maxSize - v * (maxSize - minSize))),
        slider('Brillo', 0.6, 1.5, 0.01, 1, (v) => (style.brightness = v)),
        slider('Color', 0, 2, 0.01, 1.1, (v) => (style.saturation = v)),
        h('label', { class: 'check' }, h('input', { type: 'checkbox', onChange: (e: Event) => ((style.cartoon = (e.target as HTMLInputElement).checked), draw()) }), ' Estilo cómic'),
      ],
      [
        {
          label: 'Cancelar',
          cls: 'btn ghost',
          run: () => {
            img.close();
            avatar.setFaceImage(faceCanvas);
          },
        },
        {
          label: 'Aplicar a mi personaje',
          cls: 'btn primary',
          run: async () => {
            try {
              const face = renderFace(img, crop, style);
              const blob = await encodeFace(face);
              const r = await api.uploadFace(blob);
              faceRev = r.faceRev;
              faceCanvas = face;
              faceCache.put(profile.player.id, faceRev, await createImageBitmap(face));
              img.close();
              cfg.useFace = true;
              update({ useFace: true });
              toast('¡Cara aplicada! 😎', 'success');
            } catch (e) {
              toast((e as Error).message, 'error');
              return true;
            }
          },
        },
      ],
      { wide: true },
    );
    void closeCrop;
    draw();
  };

  const render = () => {
    clear(tabs);
    const labels: Record<Tab, string> = { model: '1 · Modelo', photo: '2 · Tu cara', look: '3 · Estilo', clothes: '4 · Ropa', extras: '5 · Extras' };
    for (const t of Object.keys(labels) as Tab[]) tabs.append(h('button', { class: `tab${t === tab ? ' on' : ''}`, type: 'button', onClick: () => ((tab = t), render()) }, labels[t]));
    clear(body);
    if (tab === 'model') {
      body.append(
        optionGrid('Modelo base', BODY_MODELS, cfg.body, (id) => update({ body: id })),
        swatches('Tono de piel', SKIN_TONES, cfg.skin, (i) => update({ skin: i })),
        h('div', { class: 'opt-group' }, h('h3', null, 'Tamaño de la cabeza'), h('input', { type: 'range', min: 0.9, max: 1.3, step: 0.02, value: cfg.headScale, onChange: (e: Event) => update({ headScale: Number((e.target as HTMLInputElement).value) }) })),
      );
    } else if (tab === 'photo') body.append(photoStep());
    else if (tab === 'look') {
      body.append(
        optionGrid('Pelo', HAIR_STYLES, cfg.hair, (id) => update({ hair: id })),
        swatches('Color de pelo', HAIR_COLORS, cfg.hairColor, (i) => update({ hairColor: i })),
        optionGrid('Expresión (sin foto)', EXPRESSIONS, cfg.expression, (id) => update({ expression: id })),
        optionGrid('Gafas', GLASSES, cfg.glasses, (id) => update({ glasses: id })),
      );
    } else if (tab === 'clothes') {
      body.append(
        optionGrid('Parte de arriba', SHIRTS, cfg.shirt, (id) => update({ shirt: id })),
        swatches('Color de la camiseta', PALETTE, cfg.shirtColor, (i) => update({ shirtColor: i })),
        swatches('Pantalones', PALETTE, cfg.pantsColor, (i) => update({ pantsColor: i })),
        swatches('Zapatos', PALETTE, cfg.shoesColor, (i) => update({ shoesColor: i })),
      );
    } else {
      body.append(
        optionGrid('Sombrero', HATS, cfg.hat, (id) => update({ hat: id })),
        optionGrid('Mochila', BACKPACKS, cfg.backpack, (id) => update({ backpack: id })),
        optionGrid('Accesorio absurdo', ACCESSORIES, cfg.accessory, (id) => update({ accessory: id })),
      );
    }
  };

  const save = async () => {
    const locked = findLockedCosmetic(cfg, level);
    if (locked) return toast('Hay un cosmético bloqueado en tu selección.', 'error');
    try {
      const p = await api.saveCharacter(cfg);
      ui.set({ profile: p });
      audio.play('stage');
      toast('¡Personaje guardado!', 'success');
      close(true);
    } catch (e) {
      toast((e as Error).message, 'error');
    }
  };

  const close = (_saved: boolean) => {
    cancelAnimationFrame(raf);
    avatar.dispose();
    renderer.dispose();
    root.remove();
    window.removeEventListener('resize', resize);
    onDone();
  };

  const resize = () => {
    const r = preview.getBoundingClientRect();
    renderer.setSize(Math.max(200, r.width), Math.max(240, r.height));
    cam.aspect = Math.max(200, r.width) / Math.max(240, r.height);
    cam.updateProjectionMatrix();
  };
  window.addEventListener('resize', resize);
  document.body.append(root);
  render();
  requestAnimationFrame(() => {
    resize();
    loop();
  });
}
